const SLOT_TYPE_MAP = {
  head: ['helmet', 'hat', 'headband'],
  chest: ['armor', 'robe', 'shirt', 'chest'],
  hands: ['gloves', 'gauntlets', 'hands'],
  legs: ['pants', 'leggings', 'legs'],
  feet: ['boots', 'shoes', 'feet'],
  mainHand: ['weapon', 'sword', 'axe', 'bow', 'staff'],
  offHand: ['shield', 'weapon', 'tome'],
  ring1: ['ring'],
  ring2: ['ring'],
  necklace: ['necklace', 'amulet'],
  earring1: ['earring'],
  earring2: ['earring'],
}

const WEAPON_ITEM_TYPES = new Set(['weapon', 'sword', 'axe', 'bow', 'staff'])

export const isConsumableSlot = (slotType) => String(slotType).startsWith('consumable')

export const getConsumableIndex = (slotType) => {
  if (!isConsumableSlot(slotType)) return null
  return Number(String(slotType).replace('consumable', ''))
}

export const getEquipmentSlotValue = (equipment, slotType) => {
  if (!equipment) return null
  if (isConsumableSlot(slotType)) {
    const index = getConsumableIndex(slotType)
    return equipment.consumables?.[index] ?? null
  }
  return equipment[slotType] ?? null
}

export const setEquipmentSlotValue = (equipment, slotType, value) => {
  if (isConsumableSlot(slotType)) {
    const index = getConsumableIndex(slotType)
    if (!Array.isArray(equipment.consumables)) {
      equipment.consumables = [null, null, null, null, null]
    }
    equipment.consumables[index] = value
    return
  }
  equipment[slotType] = value
}

export const getTargetSlotFromItem = (item) => {
  if (!item?.type) return null
  const type = String(item.type).toLowerCase()

  if (isWeaponItem(item)) {
    const grip = normalizeItemGrip(item)
    if (grip === 'twoHand') return 'mainHand'
    const handSlot = normalizeItemHandSlot(item)
    if (handSlot) return handSlot
  }

  for (const [slot, types] of Object.entries(SLOT_TYPE_MAP)) {
    if (types.includes(type)) return slot
  }

  if (type === 'consumable') return 'consumable0'
  return null
}

export const isWeaponItem = (item) => WEAPON_ITEM_TYPES.has(String(item?.type || '').toLowerCase())

export const normalizeItemGrip = (item) => {
  if (!isWeaponItem(item)) return null
  const g = item?.grip != null ? String(item.grip).trim() : ''
  if (g === 'twoHand') return 'twoHand'
  return 'oneHand'
}

export const normalizeItemHandSlot = (item) => {
  if (normalizeItemGrip(item) !== 'oneHand') return null
  const hs = item?.handSlot != null ? String(item.handSlot).trim() : ''
  if (hs === 'mainHand' || hs === 'offHand') return hs
  return null
}

export const isTwoHandItem = (item) => normalizeItemGrip(item) === 'twoHand'

export const isOffHandBlockedByTwoHand = (equipment) => isTwoHandItem(equipment?.mainHand)

export const canEquipItemInSlot = (item, slotType, equipment = null) => {
  if (!item?.type) return false

  if (isConsumableSlot(slotType)) {
    return String(item.type).toLowerCase() === 'consumable'
  }

  const type = String(item.type).toLowerCase()
  const allowed = SLOT_TYPE_MAP[slotType]
  if (!allowed) return false
  if (!allowed.includes(type)) return false

  if (slotType === 'offHand' && isOffHandBlockedByTwoHand(equipment)) {
    return false
  }

  if (!isWeaponItem(item)) return true

  const grip = normalizeItemGrip(item)
  if (grip === 'twoHand') {
    return slotType === 'mainHand'
  }

  const handSlot = normalizeItemHandSlot(item)
  if (handSlot === 'mainHand') return slotType === 'mainHand'
  if (handSlot === 'offHand') return slotType === 'offHand'
  return slotType === 'mainHand' || slotType === 'offHand'
}

export const getEquippedItems = (equipment) => {
  if (!equipment) return []
  const items = []

  Object.entries(equipment).forEach(([key, value]) => {
    if (key === 'consumables' && Array.isArray(value)) {
      value.forEach((item) => {
        if (item) items.push(item)
      })
      return
    }
    if (value) items.push(value)
  })

  return items
}
